import { DynamicTable } from '@/modules/dynamic_tables'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import { VehicleColumn } from './consts'
import type { VehicleTable } from './consts'

export type VehicleColumnLabels = Record<string, string>

/**
 * What each vehicle list column is called.
 *
 * One definition for all six places the three lists are rendered -- fleet-wide
 * and on the vehicle -- and for the settings page that lets a company rename
 * them, so a shared column cannot end up with two different headers.
 */
export function useVehicleColumnLabels(): Record<
  VehicleTable,
  VehicleColumnLabels
> {
  const translations = useTranslate({
    vehicle: 'common.vehicle',
    name: 'common.name',
    type: 'common.type',
    description: 'common.description',
    expirationDate: 'documents.expiration-date',
    startDate: 'common.start-date',
    endDate: 'common.end-date',
    status: 'common.status',
    workshop: 'vehicles.service.workshop',
    cost: 'vehicles.service.cost',
    timeSpent: 'vehicles.service.time-spent',
    notificationTime: 'common.notification',
    responsibleUser: 'common.responsible-user',
  })

  const {
    vehicle,
    name,
    type,
    description,
    expirationDate,
    startDate,
    endDate,
    status,
    workshop,
    cost,
    timeSpent,
    notificationTime,
    responsibleUser,
  } = translations

  return useMemo(
    () => ({
      [DynamicTable.VEHICLE_DOCUMENTS]: {
        [VehicleColumn.VEHICLE]: vehicle,
        [VehicleColumn.NAME]: name,
        [VehicleColumn.DESCRIPTION]: description,
        [VehicleColumn.EXPIRATION_DATE]: expirationDate,
        [VehicleColumn.STATUS]: status,
        [VehicleColumn.NOTIFICATION_TIME]: notificationTime,
        [VehicleColumn.RESPONSIBLE_USER]: responsibleUser,
      },
      [DynamicTable.VEHICLE_EXPIRATIONS]: {
        [VehicleColumn.VEHICLE]: vehicle,
        [VehicleColumn.TYPE]: type,
        [VehicleColumn.DESCRIPTION]: description,
        [VehicleColumn.EXPIRATION_DATE]: expirationDate,
        [VehicleColumn.STATUS]: status,
        [VehicleColumn.NOTIFICATION_TIME]: notificationTime,
        [VehicleColumn.RESPONSIBLE_USER]: responsibleUser,
      },
      [DynamicTable.VEHICLE_SERVICES]: {
        [VehicleColumn.VEHICLE]: vehicle,
        [VehicleColumn.NAME]: name,
        [VehicleColumn.TYPE]: type,
        [VehicleColumn.SCHEDULED_START]: startDate,
        [VehicleColumn.SCHEDULED_END]: endDate,
        [VehicleColumn.STATUS]: status,
        [VehicleColumn.WORKSHOP_NAME]: workshop,
        [VehicleColumn.COST]: cost,
        [VehicleColumn.TIME_SPENT]: timeSpent,
        [VehicleColumn.NOTIFICATION_TIME]: notificationTime,
        [VehicleColumn.RESPONSIBLE_USER]: responsibleUser,
      },
    }),
    [
      cost,
      description,
      endDate,
      expirationDate,
      name,
      notificationTime,
      responsibleUser,
      startDate,
      status,
      timeSpent,
      type,
      vehicle,
      workshop,
    ]
  )
}
